import React, { useState, useEffect } from 'react';
import {
	Modal,
	Box,
	Typography,
	IconButton,
	TextField,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
	apiGetAllCauseChangeSchedule,
	apiGetAllResponsibilityChangeSchedule,
	ICauseChangeScheduleBackEnd,
	IResponsibilityChangeScheduleBackEnd,
	apiGetAllTypeChangeSchedule,
	ITypeChangeScheduleBackEnd,
} from '../services/apiService';

interface ObservationPopupProps {
	open: boolean;
	onClose: () => void;
	actionType: string;
	// Agora aceitamos um 5º parâmetro: typeId
	onSave: (
		actionType: string,
		observation: string,
		causeId: number,
		responsibilityId: number,
		typeId: number
	) => void;
	initialObservation?: string;
	notEmpty?: boolean;
}

const ObservationPopupChangeSchedule: React.FC<ObservationPopupProps> = ({
	open,
	onClose,
	actionType,
	onSave,
	initialObservation = '',
	notEmpty = true,
}) => {
	const [observation, setObservation] = useState(initialObservation);

	// Listas retornadas do backend
	const [causeChangeScheduleList, setCauseChangeScheduleList] = useState<
		ICauseChangeScheduleBackEnd[]
	>([]);
	const [
		responsibilityChangeScheduleList,
		setResponsibilityChangeScheduleList,
	] = useState<IResponsibilityChangeScheduleBackEnd[]>([]);
	const [typeChangeScheduleList, setTypeChangeScheduleList] = useState<
		ITypeChangeScheduleBackEnd[]
	>([]);

	// Estados dos selects
	const [selectedCauseId, setSelectedCauseId] = useState<number | null>(null);
	const [selectedResponsibilityId, setSelectedResponsibilityId] = useState<
		number | null
	>(null);
	const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);

	useEffect(() => {
		if (open) {
			(async () => {
				try {
					const causeList = await apiGetAllCauseChangeSchedule();
					causeList.sort((a, b) => a.description.localeCompare(b.description));
					setCauseChangeScheduleList(causeList);
				} catch (error) {
					console.error(
						'Falha ao carregar causas de mudança de cronograma',
						error
					);
				}

				try {
					const respList = await apiGetAllResponsibilityChangeSchedule();
					respList.sort((a, b) => a.description.localeCompare(b.description));
					setResponsibilityChangeScheduleList(respList);
				} catch (error) {
					console.error(
						'Falha ao carregar responsabilidades de mudança de cronograma',
						error
					);
				}

				try {
					const typeList = await apiGetAllTypeChangeSchedule();
					// Ordena alfabeticamente também
					typeList.sort((a, b) => a.description.localeCompare(b.description));
					setTypeChangeScheduleList(typeList);
				} catch (error) {
					console.error(
						'Falha ao carregar tipos de mudança de cronograma',
						error
					);
				}
			})();

			// Reseta campos ao abrir o modal
			setObservation(initialObservation);
			setSelectedCauseId(null);
			setSelectedResponsibilityId(null);
			setSelectedTypeId(null);
		}
	}, [open, initialObservation]);

	const handleSave = () => {
		// Caso precise de ao menos 1 caractere em "Observação"
		if (notEmpty && observation.trim() === '') return;

		// Como desabilitamos o botão se qualquer ID estiver null,
		// forçamos sem problemas
		onSave(
			actionType,
			observation,
			selectedCauseId as number,
			selectedResponsibilityId as number,
			selectedTypeId as number
		);
	};

	// Desabilita o botão "Salvar" se qualquer um dos 3 IDs ou a observação estiver vazio
	const isSaveButtonDisabled =
		!observation.trim() ||
		!selectedCauseId ||
		!selectedResponsibilityId ||
		!selectedTypeId;

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="observation-popup-title"
		>
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: 600,
					maxHeight: '80vh',
					bgcolor: 'background.paper',
					boxShadow: 24,
					p: 4,
					borderRadius: 2,
					overflowY: 'auto',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 2,
					}}
				>
					<Typography id="observation-popup-title" variant="h6">
						{actionType}
					</Typography>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Select - Motivo */}
				<FormControl fullWidth sx={{ mb: 2 }}>
					<InputLabel id="cause-select-label">Motivo</InputLabel>
					<Select<number | ''>
						labelId="cause-select-label"
						id="causeSelect"
						label="Motivo"
						value={selectedCauseId ?? ''}
						onChange={(e: SelectChangeEvent<number | ''>) => {
							const val = e.target.value;
							setSelectedCauseId(val === '' ? null : Number(val));
						}}
					>
						{causeChangeScheduleList.map(cause => (
							<MenuItem key={cause.id} value={cause.id}>
								{cause.description}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Select - Responsabilidade */}
				<FormControl fullWidth sx={{ mb: 2 }}>
					<InputLabel id="responsibility-select-label">
						Responsabilidade
					</InputLabel>
					<Select<number | ''>
						labelId="responsibility-select-label"
						id="responsibilitySelect"
						label="Responsabilidade"
						value={selectedResponsibilityId ?? ''}
						onChange={(e: SelectChangeEvent<number | ''>) => {
							const val = e.target.value;
							setSelectedResponsibilityId(val === '' ? null : Number(val));
						}}
					>
						{responsibilityChangeScheduleList.map(resp => (
							<MenuItem key={resp.id} value={resp.id}>
								{resp.description}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Select - Tipo */}
				<FormControl fullWidth sx={{ mb: 2 }}>
					<InputLabel id="type-select-label">Impacto no mês</InputLabel>
					<Select<number | ''>
						labelId="type-select-label"
						id="typeSelect"
						label="Impacto no mês"
						value={selectedTypeId ?? ''}
						onChange={(e: SelectChangeEvent<number | ''>) => {
							const val = e.target.value;
							setSelectedTypeId(val === '' ? null : Number(val));
						}}
					>
						{typeChangeScheduleList.map(typeItem => (
							<MenuItem key={typeItem.id} value={typeItem.id}>
								{typeItem.description}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Campo de Observação */}
				<TextField
					label="Observação"
					multiline
					rows={4}
					fullWidth
					variant="outlined"
					value={observation}
					onChange={e => setObservation(e.target.value)}
					sx={{ mb: 2 }}
				/>

				<Button
					variant="contained"
					onClick={handleSave}
					fullWidth
					disabled={isSaveButtonDisabled}
				>
					Salvar
				</Button>
			</Box>
		</Modal>
	);
};

export default ObservationPopupChangeSchedule;
